import React from 'react';
import { motion } from 'framer-motion';
import { FaSolarPanel, FaBuilding, FaIndustry, FaTools } from 'react-icons/fa';
import '../styles/Services.scss';

const Services = () => {
  const services = [
    {
      id: 'solar-bess-epc',
      icon: <FaSolarPanel size={32} />,
      title: 'SOLAR & BESS TURNKEY EPC',
      description: 'We are a future-focused Solar & BESS EPC company with 2 GW solar project experience and 1 GWh+ energy storage deployments, delivering complete engineering and construction excellence for India\'s evolving clean-energy landscape.'
    },
    {
      id: 'solar-bess-ipp',
      icon: <FaBuilding size={32} />,
      title: 'SOLAR & BESS IPP DEVELOPERS',
      description: 'We operate 0.7 GW of solar projects and 1 GWh+ of advanced storage systems—building a strong renewable energy IPP portfolio that boosts grid stability, enhances efficiency, and supports India\'s long-term sustainability goals.'
    },
    {
      id: 'module-mounting-structures',
      icon: <FaTools size={32} />,
      title: 'MODULE MOUNTING STRUCTURE MANUFACTURERS',
      description: 'We design and manufacture high-quality module mounting structures with optimized engineering and reliable performance—ensuring superior strength, durability, and project-ready efficiency.'
    },
    {
      id: 'pv-modules-cells',
      icon: <FaIndustry size={32} />,
      title: 'PV MODULES/CELLS & BESS MANUFACTURERS',
      description: 'We are building advanced manufacturing capabilities for PV modules, solar cells, and BESS systems—strengthening domestic production, energy security, and India\'s transition to sustainable, self-reliant clean power.'
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <>
      <style>
        {`
          .services-section .service-card {
            background: rgba(255, 255, 255, 0.95) !important;
            border: 2px solid rgba(14, 165, 233, 0.2) !important;
            box-shadow: 0 10px 30px rgba(14, 165, 233, 0.1) !important;
          }
          
          .services-section .service-icon {
            background: linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(56, 189, 248, 0.15) 100%) !important;
            border: 2px solid rgba(14, 165, 233, 0.2) !important;
          }
          
          .services-section .service-icon svg {
            color: #0ea5e9 !important;
          }
          
          .services-section .service-title {
            color: #0c4a6e !important;
          }
          
          .services-section .service-description {
            color: #555 !important;
          }
          
          .services-section .btn {
            background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%) !important;
            color: white !important;
            border: none !important;
            box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3) !important;
          }
          
          .services-section .service-card:hover {
            box-shadow: 0 25px 50px rgba(14, 165, 233, 0.25) !important;
            border-color: rgba(14, 165, 233, 0.5) !important;
          }
          
          .services-section .service-card:hover .service-icon {
            background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%) !important;
          }
          
          .services-section .service-card:hover .service-icon svg {
            color: white !important;
          }
        `}
      </style>
      <section className="services-section" id="services">
        <div className="container">
          {/* Section Header */}
          <motion.div
            className="section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={titleVariants}
          >
            <h2 className="section-title">Our Solar Solutions</h2>
            <p className="section-subtitle">
              Powering India's sustainable future with comprehensive solar energy solutions
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            className="row g-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
          >
            {services.map((service, index) => (
              <div key={service.id} className="col-lg-6 col-xl-3 col-md-6 col-sm-12">
                <motion.div
                  className="service-card d-flex flex-column h-100"
                  variants={cardVariants}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                >
                  {/* Service Icon */}
                  <motion.div
                    className="service-icon"
                    variants={iconVariants}
                    whileHover="hover"
                  >
                    {service.icon}
                  </motion.div>

                  {/* Service Content */}
                  <div className="flex-grow-1 d-flex flex-column">
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description flex-grow-1">
                      {service.description}
                    </p>

                    {/* CTA Button */}
                    <div className="service-cta mt-auto">
                      <motion.a
                        href="#contact"
                        className="btn"
                        whileHover={{
                          scale: 1.05,
                          transition: { duration: 0.2 }
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Learn More
                      </motion.a>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;